import React, {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FaCheck, FaArrowRight} from 'react-icons/fa';
import createServiceData from '../../services/data/serviceData';
import {Product, Service, ServiceFeature, ServiceInfo, Subservice} from '../../services/types';
import QuoteModal from "../../common/QuoteRequestModal/QuoteModal";
import NotFound from "shared/components/NotFound/NotFound";
import Seo from "shared/seo/Seo";
import { buildBreadcrumbJsonLd, buildServiceJsonLd } from "shared/seo/jsonLd";
import { getCanonicalUrl, serviceMeta } from "shared/seo/seoMeta";

import './ServiceDetail.css';


const ServiceDetail: React.FC = () => {
    const {serviceId} = useParams<{ serviceId: string }>();
    const {t} = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSubservice, setSelectedSubservice] = useState<Subservice | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    const requestQuote = (subservice: Subservice) => () => {
        setSelectedSubservice(subservice);
        setSelectedProduct(null);
        setIsModalOpen(true);
    };

    const requestProductQuote = (product: Product) => () => {
        setSelectedProduct(product);
        setSelectedSubservice(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
        setSelectedSubservice(null);
    };

    // Resolved synchronously rather than in a useEffect. The data is local, so
    // the old effect-then-setState version rendered a bare "Loading..." div on
    // first paint — and left an unknown :serviceId stuck on it forever at HTTP
    // 200, which reads as a soft 404 to Google.
    const service: Service | null = useMemo(() => {
        if (!serviceId) return null;

        const serviceData = createServiceData(t);
        return (
            serviceData[serviceId] ||
            Object.values(serviceData).find(item => item.slug === serviceId) ||
            null
        );
    }, [serviceId, t]);

    if (!service) {
        return <NotFound />;
    }

    const meta = serviceMeta[service.id];
    // Always emit a canonical, even for a service with no serviceMeta entry.
    // This route resolves both the camelCase id and the kebab slug
    // (/services/waterTreatment and /services/water-treatment), so the canonical
    // is the only thing collapsing those two URLs onto one page.
    const canonical = getCanonicalUrl(
        meta?.canonicalPath || `/services/${service.slug || service.id}`
    );
    const title = meta?.title || `${service.title} | A2S SARL`;
    const description = meta?.description || service.description;

    const content = (
        <>
            <div className="service-content-section">
                <div className="container">

                    <div className="service-header">
                        <h1>{service.title}</h1>
                    </div>


                    <div className="service-content-grid">
                        <div className="service-description">
                            <div dangerouslySetInnerHTML={{__html: service.description}}/>

                            <br/>
                            <div className="service-features">
                                <h3>{t('serviceDetail.caracteristics', 'Caractéristiques')}</h3>
                                <ul className="features-list">
                                    {service.features && service.features.map((feature: ServiceFeature, index: number) => (
                                        <li key={index}>
                                            <span className="feature-icon">{feature.icon || <FaCheck/>}</span>
                                            <div className="feature-content">
                                                <h4>{feature.title}</h4>
                                                <p>{feature.description}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>

                        <div className="service-info">
                            <img
                                className="service-image"
                                src={service.image}
                                alt={service.title}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/images/services/placeholder-service.webp';
                                }}
                            />

                            <div className="service-info-box">
                                <h3>{t('serviceDetail.whyChooseUs', 'Pourquoi nous choisir?')}</h3>
                                <ul className="info-list">
                                    {service.info && service.info.map((item: ServiceInfo, index: number) => (
                                        <li key={index}>
                                            <span className="info-icon">{item.icon || <FaCheck/>}</span>
                                            <span>{item.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {service.subservices && service.subservices.length > 0 && (
                <div className="subservices-section">
                    <div className="container">
                        <div className="section-header">
                            <h2>{t('serviceDetail.subservices', 'Nos services spécialisés')}</h2>
                            <p>{t('serviceDetail.subservicesDescription', 'Découvrez notre gamme complète de services spécialisés pour répondre à tous vos besoins.')}</p>
                        </div>

                        <div className="subservices-grid">
                            {service.subservices.map((subservice: Subservice, index: number) => (
                                <div key={index} className="subservice-card" onClick={requestQuote(subservice)}>
                                    <img
                                        className="subservice-image"
                                        src={subservice.image || service.image}
                                        alt={subservice.title}
                                        style={{objectFit: subservice.imageFit as React.CSSProperties['objectFit'] || 'cover'}}

                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = '/images/services/placeholder-service.webp';
                                        }}
                                    />
                                    <div className="subservice-content">
                                        <h3>{subservice.title}</h3>
                                        <p>{subservice.description}</p>
                                        <span className="subservice-link">
                      {t('quote.request_quote', 'Demmander un devis')} <FaArrowRight/>
                    </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}


            {/*Products section*/}
            {/*// <FeaturedProducts/>*/}
            {service.products && service.products.length > 0 && (
                <div className="subservices-section">
                    <div className="container">
                        <div className="section-header">
                            <h2>{t('serviceDetail.produits', 'Nos produits')}</h2>
                            <p>{t('serviceDetail.produitsDescription', 'Découvrez notre gamme complète de produits spécialisés pour répondre à tous vos besoins.')}</p>
                        </div>

                        <div className="subservices-grid">
                            {service.products.map((product: Product, index: number) => (
                                <div key={index} className="subservice-card" onClick={requestProductQuote(product)}>
                                    <img
                                        className="subproduct-image"
                                        src={product.image || service.image}
                                        alt={product.title}


                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = '/images/products/placeholder-product.webp';
                                        }}
                                    />
                                    <div className="subservice-content">
                                        <h3>{product.title}</h3>
                                        <p>{product.description}</p>
                                        <span className="subservice-link">
                      {t('quote.request_quote', 'Demander un devis')} <FaArrowRight/>
                    </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div className="service-detail-page">
            <Seo
                title={title}
                description={description}
                canonical={canonical}
                image={service.image}
                jsonLd={[
                    buildServiceJsonLd({
                        name: service.title,
                        description,
                        canonical,
                        image: service.image,
                    }),
                    buildBreadcrumbJsonLd([
                        {name: 'Accueil', path: '/'},
                        {name: t('nav.services', 'Services'), path: '/services'},
                        {name: service.title, path: `/services/${service.slug || service.id}`},
                    ]),
                ]}
            />
            <div className="container my-5">
                {content}
            </div>

            {isModalOpen && (
                <QuoteModal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    serviceTitle={service.title}
                    subserviceTitle={selectedSubservice ? selectedSubservice.title || '' : ''}
                    productTitle={selectedProduct ? selectedProduct.title : ''}
                />
            )}
        </div>
    );
};

export default ServiceDetail;
